import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import {
    AspectRatio,
    Box,
    Button,
    Center,
    Checkbox,
    CheckboxGroup,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    GridItem,
    HStack,
    IconButton,
    Input,
    Radio,
    RadioGroup,
    Select,
    useToast,
} from '@chakra-ui/react';
import { add, format } from 'date-fns';
import { FC, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ServiceIcon from '../../../images/icons/oil.svg';
import TachographIcon from '../../../images/icons/tachograph.svg';
import APKIcon from '../../../images/icons/tools.svg';
import { DeliveryMethods } from '../../constants/deliveryMethods';
import { PagePath, createStockDetailRoute } from '../../constants/routes';
import { TaskIdentifiers } from '../../constants/stock';
import { formatCheckboxGroupValue } from '../../helpers/checkbox';
import { isWeekday } from '../../helpers/date';
import { getMainTasks, removeMainTasks } from '../../helpers/tasks';
import { usePostAppointment } from '../../hooks/usePostAppointment';
import { usePutAppointment } from '../../hooks/usePutAppointment';
import { AppointmentInterface } from '../../types/appointment';
import { StockItemInterface } from '../../types/stock';
import { CreateTaskInterface } from '../../types/task';
import { DateInput } from '../DateInput';

type Props = {
    stockItem: StockItemInterface;
    existingAppointment: AppointmentInterface | null;
    locations: string[];
};

export const AppointmentForm: FC<Props> = ({
    stockItem,
    existingAppointment,
    locations,
}) => {
    const { mutateAsync: putMutateAsync, isPending: isPendingPut } =
        usePutAppointment();
    const { mutateAsync: postMutateAsync, isPending: isPendingPost } =
        usePostAppointment();
    const isPending = isPendingPut || isPendingPost;
    const toast = useToast();
    const emptyAdditionalTask = { task: '', remarks: '' };
    const mainTasks = getMainTasks(stockItem.type.tasks);
    const filteredTasksOptions = removeMainTasks(stockItem.type.tasks);
    const navigate = useNavigate();

    const addOneBusinessDay = (date: Date): Date => {
        const newDate = add(date, { days: 1 });

        if (!isWeekday(newDate)) {
            addOneBusinessDay(newDate);
        }
        return newDate;
    };

    const minDesiredDeliveryDate = existingAppointment
        ? new Date(existingAppointment.desiredDeliveryDate)
        : addOneBusinessDay(new Date());

    const apkDateChecked = () => {
        if (existingAppointment) {
            return (
                existingAppointment.tasks
                    .map((task) => task.task.identifier)
                    .find(
                        (identifier) => identifier === TaskIdentifiers.APK
                    ) !== undefined
            );
        }

        return stockItem.apkDateIsWithinTwoMonths;
    };

    const tachographChecked = () => {
        if (existingAppointment) {
            return (
                existingAppointment.tasks
                    .map((task) => task.task.identifier)
                    .find(
                        (identifier) =>
                            identifier === TaskIdentifiers.TACHOGRAPH
                    ) !== undefined
            );
        }

        return stockItem.tachographDateIsWithinTwoMonths;
    };

    const examinationDateChecked = () => {
        if (existingAppointment) {
            return (
                existingAppointment.tasks
                    .map((task) => task.task.identifier)
                    .find(
                        (identifier) =>
                            identifier === TaskIdentifiers.EXAMINATION
                    ) !== undefined
            );
        }

        return stockItem.examinationDateIsWithinTwoMonths;
    };

    const tailgateDateChecked = () => {
        if (existingAppointment) {
            return (
                existingAppointment.tasks
                    .map((task) => task.task.identifier)
                    .find(
                        (identifier) => identifier === TaskIdentifiers.TAILGATE
                    ) !== undefined
            );
        }

        return stockItem.tailgateDateIsWithinTwoMonths;
    };

    const cfkExaminationDateChecked = () => {
        if (existingAppointment) {
            return (
                existingAppointment.tasks
                    .map((task) => task.task.identifier)
                    .find(
                        (identifier) =>
                            identifier === TaskIdentifiers.CFK_EXAMINATION
                    ) !== undefined
            );
        }

        return stockItem.cfkExaminationDateDateIsWithinTwoMonths;
    };

    const getDefaultTasks = () => {
        let defaultTasks: string[] = [];

        if (apkDateChecked()) {
            defaultTasks.push(TaskIdentifiers.APK);
            defaultTasks.push(TaskIdentifiers.SERVICE);
        }

        if (tachographChecked()) {
            defaultTasks.push(TaskIdentifiers.TACHOGRAPH);
        }

        if (examinationDateChecked()) {
            defaultTasks.push(TaskIdentifiers.EXAMINATION);
        }

        if (tailgateDateChecked()) {
            defaultTasks.push(TaskIdentifiers.TAILGATE);
        }

        if (cfkExaminationDateChecked()) {
            defaultTasks.push(TaskIdentifiers.CFK_EXAMINATION);
        }

        return defaultTasks;
    };

    const getAdditionalTasks = () => {
        if (existingAppointment) {
            const _tasks = existingAppointment.tasks.map((task) => {
                return {
                    id: task.task.id,
                    title: task.task.title,
                    identifier: task.task.identifier,
                    remarks: task.remarks,
                };
            });

            const _additional = removeMainTasks(_tasks);

            return _additional.map((task) => {
                return {
                    task: task.id.toString(),
                    // @ts-ignore
                    remarks: task.remarks ?? '',
                };
            });
        }

        return [emptyAdditionalTask];
    };

    const getDefaultValues = (
        existingAppointment: AppointmentInterface | null
    ) => {
        if (existingAppointment) {
            const timeRegex = new RegExp('.*T(.*)\\+(?:.*)');
            const time =
                timeRegex.exec(existingAppointment.desiredDeliveryDate)?.[1] ??
                '';

            const additionalTasks = getAdditionalTasks();
            const tasks = getDefaultTasks();
            const hasAdditionalTasks = additionalTasks.length > 0;

            if (hasAdditionalTasks) {
                tasks.push('additionalTasksEnabled');
            }

            return {
                deliveryMethod: existingAppointment.deliveryMethod ?? '',
                desiredDeliveryDate: existingAppointment.desiredDeliveryDate
                    ? format(
                          new Date(existingAppointment.desiredDeliveryDate),
                          'yyyy-MM-dd'
                      )
                    : '',
                pickUpLocation: existingAppointment.pickUpLocation ?? '',
                reference: existingAppointment.reference ?? '',
                remarks: existingAppointment.remarks ?? '',
                timePeriod: time ?? '',
                tasks: tasks,
                additionalTasks: hasAdditionalTasks
                    ? additionalTasks
                    : [emptyAdditionalTask],
            };
        }

        const defaultValues = {
            deliveryMethod: '',
            desiredDeliveryDate: '',
            pickUpLocation: '',
            reference: '',
            remarks: '',
            timePeriod: '',
            tasks: getDefaultTasks(),
            additionalTasks: [emptyAdditionalTask],
        };

        return defaultValues;
    };

    const {
        control,
        watch,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: getDefaultValues(existingAppointment),
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'additionalTasks',
    });

    useEffect(() => {
        reset(getDefaultValues(existingAppointment));
    }, [stockItem, existingAppointment]);

    if (stockItem.type.tasks.length === 0) {
        return null;
    }

    const desiredDeliveryDateValue = watch('desiredDeliveryDate');
    const tasksValue = watch('tasks');
    const additionalTasksEnabled = tasksValue.includes(
        'additionalTasksEnabled'
    );
    const deliveryMethodValue = watch('deliveryMethod');

    const getIconForTask = (identifier: string) => {
        switch (identifier) {
            case TaskIdentifiers.SERVICE:
                return <ServiceIcon />;
            case TaskIdentifiers.TACHOGRAPH:
                return <TachographIcon />;
            case TaskIdentifiers.APK:
            default:
                return <APKIcon />;
        }
    };

    const getDisabledForTask = (identifier: string) => {
        switch (identifier) {
            case TaskIdentifiers.APK:
            case TaskIdentifiers.SERVICE:
                return stockItem.apkDateIsWithinTwoMonths;
            case TaskIdentifiers.TACHOGRAPH:
                return stockItem.tachographDateIsWithinTwoMonths;
            case TaskIdentifiers.EXAMINATION:
                return stockItem.examinationDateIsWithinTwoMonths;
            case TaskIdentifiers.TAILGATE:
                return stockItem.tailgateDateIsWithinTwoMonths;
            case TaskIdentifiers.CFK_EXAMINATION:
                return stockItem.cfkExaminationDateDateIsWithinTwoMonths;
            default:
                return false;
        }
    };

    const onSubmit = async (values: {
        deliveryMethod: string;
        pickUpLocation: string;
        tasks: string[];
        desiredDeliveryDate: string;
        timePeriod: string;
        reference: string;
        remarks: string;
        additionalTasks: {
            task: string;
            remarks: string;
        }[];
    }) => {
        const _tasks: CreateTaskInterface[] = [
            ...mainTasks
                .filter((task) => {
                    // get full task objects based on identifier
                    if (!task.identifier) {
                        return false;
                    }

                    return values.tasks.includes(task.identifier);
                })
                .map((task) => {
                    return {
                        task: task.id as number,
                        remarks: null,
                    };
                }),
            ...values.additionalTasks
                .filter((task) => {
                    // remove empty additionalTasks
                    return !!task.task;
                })
                .map((task) => {
                    return {
                        task: parseInt(task.task),
                        remarks: task.remarks ?? null,
                    };
                }),
        ];

        const payload = {
            tasks: _tasks,
            desiredDeliveryDate: `${values.desiredDeliveryDate}T${values.timePeriod}`,
            remarks: values.remarks ?? null,
            reference: values.reference ?? null,
            deliveryMethod: values.deliveryMethod ?? null,
            pickUpLocation:
                values.deliveryMethod === DeliveryMethods.PICKUP
                    ? values.pickUpLocation
                    : null,
        };

        if (existingAppointment) {
            try {
                await putMutateAsync({
                    appointmentId: existingAppointment.id,
                    updateAppointment: payload,
                });

                navigate(createStockDetailRoute(stockItem.stockNumber));

                return toast({
                    title: 'Gelukt! We hebben je afspraak in goede orde ontvangen.',
                    status: 'success',
                    duration: 4000,
                    isClosable: true,
                });
            } catch (e) {
                toast({
                    title: 'Er ging iets mis met het versturen van je afspraak.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
        }

        try {
            await postMutateAsync({
                createAppointment: { ...payload, stock: stockItem.stockNumber },
            });

            navigate(PagePath.StockOverview);

            return toast({
                title: 'Gelukt! We hebben je afspraak in goede orde ontvangen.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (e) {
            toast({
                title: 'Er ging iets mis met het versturen van je afspraak.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl
                mb={4}
                isDisabled={isPending}
                isInvalid={!!errors.tasks}
            >
                <FormLabel>Taken</FormLabel>
                <Controller
                    name={'tasks'}
                    control={control}
                    render={({ field: { ref, onChange, ...field } }) => (
                        <CheckboxGroup
                            {...field}
                            onChange={(val) =>
                                onChange(formatCheckboxGroupValue(val))
                            }
                        >
                            <Grid
                                gridTemplateColumns={{
                                    base: 'repeat(2, 1fr)',
                                    md: 'repeat(4, 1fr)',
                                    lg: 'repeat(5, 1fr)',
                                }}
                                gap={4}
                            >
                                {mainTasks.map((task) => {
                                    if (!task.identifier) {
                                        return null;
                                    }

                                    return (
                                        <GridItem key={task.id}>
                                            <Checkbox
                                                value={task.identifier}
                                                ref={ref}
                                                variant={'icon'}
                                                isDisabled={getDisabledForTask(
                                                    task.identifier
                                                )}
                                            >
                                                <Box
                                                    px={8}
                                                    pt={8}
                                                    pb={4}
                                                    w={'full'}
                                                    color={'primary.500'}
                                                >
                                                    <AspectRatio ratio={1}>
                                                        <Center>
                                                            {getIconForTask(
                                                                task.identifier
                                                            )}
                                                        </Center>
                                                    </AspectRatio>
                                                </Box>
                                                {task.title}
                                            </Checkbox>
                                        </GridItem>
                                    );
                                })}

                                <GridItem>
                                    <Checkbox
                                        value={'additionalTasksEnabled'}
                                        ref={ref}
                                        variant={'icon'}
                                    >
                                        <Box
                                            px={8}
                                            pt={8}
                                            pb={4}
                                            w={'full'}
                                            color={'primary.500'}
                                        >
                                            <AspectRatio ratio={1}>
                                                <Center>
                                                    <AddIcon boxSize={8} />
                                                </Center>
                                            </AspectRatio>
                                        </Box>
                                        Extra taken
                                    </Checkbox>
                                </GridItem>
                            </Grid>
                        </CheckboxGroup>
                    )}
                />
                <FormErrorMessage>{errors.tasks?.message}</FormErrorMessage>
            </FormControl>

            {!!additionalTasksEnabled && (
                <>
                    {fields.map((field, index) => (
                        <Flex key={field.id} gap={4}>
                            <Box flex={1}>
                                <FormControl
                                    mb={4}
                                    isDisabled={isPending}
                                    // isInvalid={!!errors.additionalTasksEnabled}
                                >
                                    <FormLabel>Kies taak</FormLabel>
                                    <Select
                                        placeholder="Kies taak"
                                        {...register(
                                            `additionalTasks.${index}.task`
                                        )}
                                    >
                                        {filteredTasksOptions.map((task) => {
                                            return (
                                                <option
                                                    value={task.id}
                                                    key={task.id}
                                                >
                                                    {task.title}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                <FormControl
                                    mb={4}
                                    isDisabled={isPending}
                                    // isInvalid={!!errors.additionalTasksEnabled}
                                >
                                    <FormLabel>Opmerkingen</FormLabel>
                                    <Input
                                        {...register(
                                            `additionalTasks.${index}.remarks`
                                        )}
                                        autoComplete="off"
                                    />
                                </FormControl>
                            </Box>

                            <Box>
                                <FormLabel>&nbsp;</FormLabel>
                                {index === fields.length - 1 ? (
                                    <IconButton
                                        aria-label={'Voeg extra taak toe'}
                                        icon={<AddIcon />}
                                        onClick={() =>
                                            append(emptyAdditionalTask)
                                        }
                                    />
                                ) : (
                                    <IconButton
                                        aria-label={'Verwijder taak'}
                                        icon={<MinusIcon />}
                                        onClick={() => remove(index)}
                                    />
                                )}
                            </Box>
                        </Flex>
                    ))}
                </>
            )}

            <FormControl
                mb={4}
                isDisabled={isPending}
                isInvalid={!!errors.desiredDeliveryDate}
            >
                <FormLabel>Gewenste aanleverdatum</FormLabel>
                <Controller
                    control={control}
                    name={'desiredDeliveryDate'}
                    rules={{
                        required: 'Verplicht veld',
                    }}
                    render={({ field: { onChange, value, disabled } }) => (
                        <DateInput
                            onChange={(val) => {
                                const dateString = val
                                    ? format(val, 'yyyy-MM-dd')
                                    : '';
                                onChange(dateString);
                            }}
                            value={value ? new Date(value) : null}
                            disabled={disabled}
                            minDate={minDesiredDeliveryDate}
                        />
                    )}
                />
                <FormErrorMessage>
                    {errors.desiredDeliveryDate?.message}
                </FormErrorMessage>
            </FormControl>

            {!!desiredDeliveryDateValue && (
                <FormControl
                    mb={4}
                    isDisabled={isPending}
                    isInvalid={!!errors.timePeriod}
                >
                    <Controller
                        control={control}
                        name="timePeriod"
                        rules={{
                            required: 'Verplicht veld',
                        }}
                        render={({ field }) => (
                            <RadioGroup {...field}>
                                <HStack>
                                    <Radio value="08:00:00" variant={'card'}>
                                        Ochtend 08:00 - 12:00
                                    </Radio>
                                    <Radio value="13:00:00" variant={'card'}>
                                        Middag 13:00 - 17:30
                                    </Radio>
                                </HStack>
                            </RadioGroup>
                        )}
                    />
                    <FormErrorMessage>
                        {errors.timePeriod?.message}
                    </FormErrorMessage>
                </FormControl>
            )}

            <FormControl mb={4} isDisabled={isPending}>
                <FormLabel>PO-nummer / Referentie</FormLabel>
                <Input {...register('reference')} />
            </FormControl>

            <FormControl mb={4} isDisabled={isPending}>
                <FormLabel>Opmerkingen</FormLabel>
                <Input {...register('remarks')} />
            </FormControl>

            {locations && locations.length > 0 && (
                <FormControl
                    mb={4}
                    isDisabled={isPending}
                    isInvalid={!!errors.deliveryMethod}
                >
                    <FormLabel>Voertuig ophalen of wegbrengen</FormLabel>
                    <Select
                        {...register('deliveryMethod', {
                            validate: (value) => {
                                if (locations.length > 0) {
                                    return !!value ? true : 'Verplicht veld';
                                }

                                return true;
                            },
                        })}
                        placeholder="Selecteer..."
                    >
                        <option value={DeliveryMethods.DROPOFF}>
                            Wegbrengen
                        </option>
                        <option value={DeliveryMethods.PICKUP}>Ophalen</option>
                    </Select>
                    <FormErrorMessage>
                        {errors.deliveryMethod?.message}
                    </FormErrorMessage>
                </FormControl>
            )}

            {deliveryMethodValue === DeliveryMethods.PICKUP && (
                <FormControl
                    mb={4}
                    isDisabled={isPending}
                    isInvalid={!!errors.pickUpLocation}
                >
                    <FormLabel>Ophaal locatie</FormLabel>
                    <Select
                        {...register('pickUpLocation', {
                            validate: (value) => {
                                if (
                                    deliveryMethodValue ===
                                    DeliveryMethods.PICKUP
                                ) {
                                    return !!value
                                        ? true
                                        : 'Selecteer een locatie waar het voertuig opgehaald moet worden.';
                                }

                                return true;
                            },
                        })}
                        placeholder="Selecteer..."
                    >
                        {locations?.map((location) => (
                            <option key={location} value={location}>
                                {location}
                            </option>
                        ))}
                    </Select>
                    <FormErrorMessage>
                        {errors.pickUpLocation?.message}
                    </FormErrorMessage>
                </FormControl>
            )}

            <Button
                type={'submit'}
                colorScheme={'primary'}
                isLoading={isPending}
            >
                {existingAppointment
                    ? 'Afspraak aanpassen'
                    : 'Afspraak indienen'}
            </Button>
        </form>
    );
};
